<template>
  <div class="mb-3 flex flex-col gap-2">
    <!-- Horizontal scrollable chips row — only shown when files are attached -->
    <div v-if="attachedFiles.length > 0 || pendingFiles.length > 0"
      class="flex gap-2! overflow-x-auto pb-3.5 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
      <FileChip v-for="file in [...attachedFiles, ...pendingFiles]" :key="file.id" :file="file"
        @remove="handleDetachClick(file.id, props.conversationId)" @delete="handleDeleteFile(file.id)" />
    </div>

    <!-- Action row: buttons on left, placeholder text fills the right -->
    <div class="flex items-center gap-2">
      <input ref="fileInput" type="file" accept=".xlsx,.xls" @change="handleFileSelect" class="hidden" />

      <!-- Upload button -->
      <button @click="$refs.fileInput.click()" :disabled="isUploading"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-nera-border text-nera-text transition-colors cursor-pointer hover:bg-nera-light-hover hover:border-nera-accent/40 disabled:opacity-60 disabled:cursor-not-allowed">
        <i :class="isUploading ? 'fas fa-spinner fa-spin' : 'fas fa-upload'" class="text-xs"></i>
        <span>Upload</span>
      </button>

      <!-- Library dropdown -->
      <div class="relative" ref="dropdownRef">
        <button @click="toggleDropdown"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-nera-accent border border-nera-accent text-white transition-colors cursor-pointer hover:bg-nera-accent-hover hover:border-nera-accent-hover shadow-sm"
          aria-haspopup="true" :aria-expanded="showDropdown">
          <i class="fas fa-link text-xs"></i>
          <span>Library</span>
          <i class="fas fa-chevron-up text-[0.65rem] transition-transform duration-200"
            :class="{ 'rotate-180': showDropdown }"></i>
        </button>

        <Transition
          enter-active-class="transition duration-150 ease-out"
          leave-active-class="transition duration-100 ease-in"
          enter-from-class="opacity-0 translate-y-1"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div v-show="showDropdown" role="menu"
            class="absolute bottom-full left-0 mb-2 w-80 z-50 rounded-lg overflow-hidden bg-white border border-nera-border shadow-lg">
            <div class="px-3 py-2 border-b border-nera-border bg-nera-elevated-2">
              <p class="m-0 text-xs font-semibold uppercase tracking-wider text-nera-muted">Your files</p>
            </div>
            <div
              class="max-h-72 overflow-y-auto py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-nera-border [&::-webkit-scrollbar-thumb:hover]:bg-nera-light-border-strong"
            >
              <div v-if="loadingFiles" class="flex items-center gap-2 px-3 py-3 text-sm text-nera-muted">
                <i class="fas fa-spinner fa-pulse"></i>
                Loading...
              </div>
              <div v-else-if="availableFiles.length === 0" class="px-3 py-6 text-sm text-center text-nera-muted">
                No files in your library yet
              </div>
              <template v-else>
                <div v-for="file in availableFiles" :key="file.id"
                  class="group flex items-center gap-2 mx-1 px-2 py-2 rounded-md transition-colors hover:bg-nera-light-hover">
                  <i :class="getFileIcon(file.type)" class="text-base text-nera-muted shrink-0"></i>
                  <button @click="handleAttachExisting(file.id)"
                    class="flex-1 min-w-0 flex flex-col items-start text-left cursor-pointer bg-transparent border-0 p-0">
                    <span class="w-full truncate text-sm font-medium text-nera-text">{{ file.name }}</span>
                    <span class="text-xs text-nera-muted">{{ formatSize(file.size) }}</span>
                  </button>
                  <button @click="handleDeleteFile(file.id)"
                    class="inline-flex items-center justify-center w-7 h-7 rounded-md text-nera-muted opacity-0 group-hover:opacity-100 transition-all shrink-0 hover:bg-nera-danger/10 hover:text-nera-danger"
                    aria-label="Delete permanently" title="Delete permanently">
                    <i class="fas fa-trash-alt text-xs"></i>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </Transition>
      </div>

      <div class="ml-auto">
        <ModelSelector />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import FileChip from './FileChip.vue'
import { onClickOutside } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useConversationsStore } from '../stores/conversations'
import { useToast } from 'vue-toastification'
import ModelSelector from './ModelSelector.vue'

const toast = useToast()
const conversationsStore = useConversationsStore()

const props = defineProps({
  conversationId: {
    type: Number,
    required: false
  }
})

const fileInput = ref(null)
const isUploading = ref(false)
const showDropdown = ref(false)
const loadingFiles = ref(false)
const availableFiles = ref([])
const dropdownRef = ref(null)

const { attachedFiles, pendingFiles } = storeToRefs(conversationsStore)
const { detachFile } = conversationsStore

onClickOutside(dropdownRef, () => {
  showDropdown.value = false
})

const handleFileSelect = async (e) => {
  const file = e.target.files[0]
  if (!file) return

  isUploading.value = true

  try {
    const formData = new FormData()
    formData.append('file', file)

    const csrfToken = document.querySelector('[name="csrf-token"]')?.content
    const checkResponse = await fetch('/api/files/check_duplicate', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
      body: formData
    })

    const checkResult = await checkResponse.json()

    if (checkResult.exists) {
      await handleAttachExisting(checkResult.file.id)
      alert(`File "${checkResult.file.name}" already exists. Linked to conversation.`)
    } else {
      const uploadResponse = await fetch(
        `/api/conversations/${props.conversationId}/files/upload`,
        {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken },
          body: formData
        }
      )
      if (uploadResponse.ok) {
        const fileData = await uploadResponse.json()
        await handleAttachExisting(fileData.id, fileData)
      } else {
        const error = await uploadResponse.json()
        alert(error.error || 'Upload failed')
      }
    }
  } catch (error) {
    console.error('Upload error:', error)
    alert('Upload failed')
  } finally {
    isUploading.value = false
    e.target.value = ''
  }
}

const handleAttachExisting = async (fileId, fileData = null) => {
  if (!props.conversationId) {
    if (fileData) {
      if (!pendingFiles.value.some(f => f.id === fileId)) {
        pendingFiles.value = [...pendingFiles.value, fileData]
      }
    } else {
      const fileFromList = availableFiles.value.find(f => f.id === fileId)
      if (fileFromList) {
        const isAttached = attachedFiles.value.some(f => f.id === fileId)
        const isPending = pendingFiles.value.some(f => f.id === fileId)
        if (!isAttached && !isPending) {
          pendingFiles.value = [...pendingFiles.value, fileFromList]
        }
      }
    }
    showDropdown.value = false
    return
  }
  try {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content
    const response = await fetch(
      `/api/conversations/${props.conversationId}/files/attach`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ file_id: fileId })
      }
    )

    if (response.ok) {
      const fileData = await response.json()
      attachedFiles.value = [...attachedFiles.value, fileData]
      showDropdown.value = false
    } else {
      const error = await response.json()
      if (error.error && error.error.includes('already attached')) {
        showDropdown.value = false
      } else {
        alert(error.error || 'Failed to attach file')
      }
    }
  } catch (error) {
    console.error('Attach error:', error)
    alert('Failed to attach file')
  }
}

const handleDetachClick = async (fileId, conversationId) => {
  try {
    detachFile(fileId, conversationId)
  } catch (error) {
    console.error('Remove error:', error)
    toast.error(error.message)
  }
}

const handleDeleteFile = async (fileId) => {
  if (!confirm('Are you sure you want to permanently delete this file? This cannot be undone.')) return

  try {
    availableFiles.value = availableFiles.value.filter(f => f.id !== fileId)
    pendingFiles.value = pendingFiles.value.filter(f => f.id !== fileId)
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content
    const response = await fetch(`/api/files/${fileId}/destroy`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken }
    })

    if (response.ok) {
      attachedFiles.value = attachedFiles.value.filter(f => f.id !== fileId)
    } else {
      const error = await response.json()
      alert(error.error || 'Delete failed')
      availableFiles.value = [...availableFiles.value, fileId]
    }
  } catch (error) {
    console.error('Delete error:', error)
    alert('Delete failed')
  }
}

const toggleDropdown = async () => {
  if (!showDropdown.value) await loadAvailableFiles()
  showDropdown.value = !showDropdown.value
}

const loadAvailableFiles = async () => {
  loadingFiles.value = true
  try {
    const response = await fetch('/api/files', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (response.ok) {
      const files = await response.json()
      const attachedIds = attachedFiles.value.map(f => f.id)
      const pendingIds = pendingFiles.value.map(f => f.id)
      availableFiles.value = files.filter(f => !attachedIds.includes(f.id) && !pendingIds.includes(f.id))
    }
  } catch (error) {
    console.error('Failed to load files:', error)
  } finally {
    loadingFiles.value = false
  }
}

const getFileIcon = (type) => {
  if (type?.includes('csv')) return 'fas fa-file-csv'
  if (type?.includes('excel') || type?.includes('spreadsheet')) return 'fas fa-file-excel'
  return 'fas fa-file'
}

const formatSize = (bytes) => {
  if (!bytes) return '0 B'
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

defineExpose({ handleFileUpload: handleFileSelect })
</script>
